import {
  EmptyState,
  StatusTag,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from '@westhetics/ui'
import { ListFooter } from '@/app/list-footer'
import { LoadError } from '@/app/load-error'
import { useAutentiqueEvents } from './api'
import { ago, autentiqueEventLabel, documentText, eventResult } from './labels'

/**
 * Avisos da Autentique (RN-ADM-99): o "Histórico de eventos processados" do legado, de todos os tempos, os
 * mais recentes primeiro, com o resultado aqui e a situação do documento. Nunca o corpo do aviso.
 */
export function AutentiqueEventsSection({
  page,
  now,
  onPageChange,
}: {
  page: number
  now: number
  onPageChange: (page: number) => void
}) {
  const query = useAutentiqueEvents({ page })
  const items = query.data?.data ?? []

  return (
    <section aria-labelledby="operations-autentique" className="flex flex-col gap-4">
      <h2 id="operations-autentique" className="eyebrow">
        Avisos da Autentique
      </h2>

      {query.isError ? (
        <LoadError
          title="Não conseguimos carregar os avisos da Autentique"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : query.isPending ? (
        <TableSkeleton rows={4} columns={6} />
      ) : items.length === 0 ? (
        <EmptyState
          title="Nenhum aviso da Autentique ainda"
          description="Os avisos chegam quando a Autentique entrega, lê ou assina um documento enviado por ela."
        />
      ) : (
        <Table>
          <TableHeader>
            <tr>
              <TableHead>Quando</TableHead>
              <TableHead>Documento na Autentique</TableHead>
              <TableHead>Clínica</TableHead>
              <TableHead>Aviso</TableHead>
              <TableHead>Resultado</TableHead>
              <TableHead>Documento</TableHead>
            </tr>
          </TableHeader>
          <TableBody>
            {items.map((event) => {
              const result = eventResult(event)
              const document = documentText(event)
              return (
                <TableRow key={event.id}>
                  <TableCell label="Quando" muted>
                    {ago(event.receivedAt, now)}
                  </TableCell>
                  <TableCell label="Documento na Autentique" muted className="break-all">
                    {event.documentReference ? (
                      <code className="font-body text-[12px] font-light">
                        {event.documentReference}
                      </code>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell label="Clínica" muted={!event.clinic}>
                    {event.clinic?.name ?? '—'}
                  </TableCell>
                  <TableCell primary>{autentiqueEventLabel(event.type)}</TableCell>
                  <TableCell label="Resultado">
                    <span className="flex flex-col items-start gap-1">
                      <StatusTag level={result.level}>{result.label}</StatusTag>
                      {result.note ? (
                        <span className="text-secondary text-[12px] leading-[1.5]">
                          {result.note}
                        </span>
                      ) : null}
                    </span>
                  </TableCell>
                  <TableCell label="Documento" muted={!document}>
                    {document ?? '—'}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}
      {query.data && items.length > 0 ? (
        <ListFooter
          meta={query.data.meta}
          noun={['aviso', 'avisos']}
          note="os mais recentes primeiro"
          onPage={onPageChange}
        />
      ) : null}
    </section>
  )
}
